import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnInit, DestroyRef, input, inject } from '@angular/core';
import { isEqual, isNil, omitBy } from 'lodash';
import { IrisDMSFileI } from '@iris/common/modules/dms/models/IrisDMSFile';
import { IInfiniteScrollEvent, InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AbstractDatetime } from '@iris/common/modules/date';
import { debounceTime, skip, startWith, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IrisDmsContentPageFilterI } from '../../../models/IrisDmsContentPagination';
import { distinctUntilPropertyChanged } from '@iris/common/utils/rxjs.utils';
import { TranslatePipe } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IrisLoaderModule } from '@iris/common/modules/loader';
import { IrisEmptyContentViewComponent } from '../../empty-content-view/empty-content-view.component';
import { IrisTileViewFileCardComponent } from '../tile-view-file-card/tile-view-file-card.component';
import { ResizeObserverDirective } from '@ngx-strakit/cdk';
import {
  MatDatetimepickerType,
  StrakitButtonComponent,
  StrakitDatetimeComponent,
  StrakitTextComponent,
} from '@ngx-strakit/cdk/controls';

@Component({
  selector: 'iris-tile-view-lite',
  imports: [
    ReactiveFormsModule,
    InfiniteScrollDirective,
    TranslatePipe,
    MatTooltipModule,
    StrakitTextComponent,
    IrisLoaderModule,
    ResizeObserverDirective,
    IrisEmptyContentViewComponent,
    IrisTileViewFileCardComponent,
    StrakitDatetimeComponent,
    StrakitButtonComponent,
  ],
  templateUrl: './tile-view-lite.component.html',
  styleUrls: ['./tile-view-lite.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisTileViewLiteComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Input() items: IrisDMSFileI[] = [];
  @Input() selectedFiles: IrisDMSFileI[] = [];
  @Input() multiple = true;
  readonly loading = input(false);

  @Output() scrolled = new EventEmitter<IInfiniteScrollEvent>();
  @Output() selectedFilesChanged = new EventEmitter<IrisDMSFileI[]>();
  @Output() filterChanged = new EventEmitter<IrisDmsContentPageFilterI>();

  readonly datePickerType: MatDatetimepickerType = 'date';

  readonly filterFormGroup = new FormGroup({
    name: new FormControl<string>(''),
    from: new FormControl<Date | string>(null),
    to: new FormControl<Date | string>(null),
  });

  ngOnInit(): void {
    this.handleFilterFormChanges();
  }

  isFileSelected(file: IrisDMSFileI): boolean {
    return this.selectedFiles.some(({ id }) => id === file.id);
  }

  fileSelectionChanged(selected: boolean, file: IrisDMSFileI): void {
    if (!this.multiple && selected) {
      this.selectedFilesChanged.emit([file]);
      return;
    }
    let selectedFiles = [...this.selectedFiles];
    if (selected && !this.isFileSelected(file)) {
      selectedFiles.push(file);
    }
    if (!selected && this.isFileSelected(file)) {
      selectedFiles = selectedFiles.filter(({ id }) => id !== file.id);
    }
    if (!isEqual(selectedFiles, this.selectedFiles)) {
      this.selectedFilesChanged.emit(selectedFiles);
    }
  }

  onScroll($event: IInfiniteScrollEvent): void {
    this.scrolled.emit($event);
  }

  tileViewSizeChanged(event: ResizeObserverEntry[]): void {
    const entry = event[0];
    if (!entry.contentRect.height) {
      return;
    }

    if (entry.target.scrollHeight <= entry.contentRect.height) {
      this.onScroll({ currentScrollPosition: entry.target.scrollTop });
    }
  }

  resetFilters(): void {
    this.filterFormGroup.reset();
  }

  private handleFilterFormChanges(): void {
    this.filterFormGroup.valueChanges.pipe(
      debounceTime(300),
      startWith(this.filterFormGroup.value),
      distinctUntilPropertyChanged(),
      skip(1),
      tap(() => {
        const { name, from, to } = this.filterFormGroup.value;
        const filter = omitBy<IrisDmsContentPageFilterI>({
          name: name ? name : null,
          updatedOn: {
            from: this.formatDate(from as Date),
            to: this.formatDate(to as Date, true),
          },
        }, isNil);
        this.filterChanged.emit(filter);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  private formatDate(date: Date, endOfDay = false): Date {
    if (!date) { return date; }
    const formattedDate = AbstractDatetime.parse(date).utc();
    if (endOfDay) {
      return formattedDate.endOf('day').toJSDate();
    }
    return formattedDate.startOf('day').toJSDate();
  }
}
